import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Award, Star, TrendingUp, Gift, Trophy, Medal, Users } from 'lucide-react';
import { api } from '@/lib/api';

interface LoyaltyStats {
  id: number;
  name: string;
  currentPoints: number;
  level: string;
  totalEarned: number;
  totalRedeemed: number;
  nextLevel: string;
  pointsToNextLevel: number;
  history: any[];
}

interface LeaderboardItem {
  id: number;
  name: string;
  points: number;
  level: string;
}

export default function LoyaltyPage() {
  const [stats, setStats] = useState<LoyaltyStats | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardItem[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(1);
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (selectedCustomerId) {
      fetchLoyaltyData();
    }
  }, [selectedCustomerId]);

  const fetchCustomers = async () => {
    try {
      const res = await api.get('/api/customers');
      const data = res.data?.data || res.data?.items || [];
      setCustomers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchLoyaltyData = async () => {
    try {
      setLoading(true);
      const [statsRes, leaderboardRes] = await Promise.all([
        api.get(`/api/loyalty/stats/${selectedCustomerId}`),
        api.get('/api/loyalty/leaderboard')
      ]);

      if (statsRes.data?.success) {
        setStats(statsRes.data.data);
      }
      if (leaderboardRes.data?.success) {
        setLeaderboard(leaderboardRes.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching loyalty data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Platinum': return 'from-purple-500 to-indigo-600';
      case 'Gold': return 'from-yellow-400 to-amber-600';
      case 'Silver': return 'from-gray-300 to-gray-500';
      default: return 'from-orange-400 to-orange-600';
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'Platinum': return <Trophy className="w-8 h-8" />;
      case 'Gold': return <Award className="w-8 h-8" />;
      case 'Silver': return <Medal className="w-8 h-8" />;
      default: return <Star className="w-8 h-8" />;
    }
  };

  const getLevelProgress = () => {
    if (!stats) return 0;
    const levelThresholds: Record<string, number> = {
      'Bronze': 1000,
      'Silver': 5000,
      'Gold': 10000,
      'Platinum': 10000
    };
    const currentThreshold = levelThresholds[stats.level] || 1000;
    const previousThreshold = stats.level === 'Bronze' ? 0 : 
                             stats.level === 'Silver' ? 1000 :
                             stats.level === 'Gold' ? 5000 : 10000;
    const progress = ((stats.currentPoints - previousThreshold) / (currentThreshold - previousThreshold)) * 100;
    return Math.min(100, Math.max(0, progress));
  };

  return (
    <div className="p-6 space-y-6" dir="rtl">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
              <Award className="w-6 h-6 text-white" />
            </div>
            برنامج الولاء
          </h1>
          <p className="text-muted-foreground mt-1">نظام المكافآت والنقاط للعملاء</p>
        </div>
        
        <select
          value={selectedCustomerId}
          onChange={(e) => setSelectedCustomerId(Number(e.target.value))}
          className="px-4 py-2 border rounded-xl bg-white"
        >
          {customers.map(c => (
            <option key={c.id} value={c.id}>{c.fullName || c.name || `عميل #${c.id}`}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p>جاري التحميل...</p>
        </div>
      ) : stats ? (
        <>
          {/* البطاقة الرئيسية */}
          <div className={`rounded-3xl p-8 text-white bg-gradient-to-br ${getLevelColor(stats.level)} shadow-2xl`}>
            <div className="flex items-center justify-between flex-wrap gap-6">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur">
                  {getLevelIcon(stats.level)}
                </div>
                <div>
                  <p className="text-white/80 text-sm">المستوى الحالي</p>
                  <h2 className="text-4xl font-bold">{stats.level}</h2>
                  <p className="text-white/90 mt-1">{stats.name}</p>
                </div>
              </div>
              <div className="text-left">
                <p className="text-white/80 text-sm">النقاط الحالية</p>
                <p className="text-5xl font-bold">{stats.currentPoints.toLocaleString()}</p>
                <p className="text-white/90 text-sm mt-1">نقطة</p>
              </div>
            </div>

            {/* شريط التقدم */}
            <div className="mt-8">
              <div className="flex justify-between text-sm mb-2">
                <span>{stats.level}</span>
                <span>{stats.nextLevel === 'Max' ? 'أقصى مستوى' : stats.nextLevel}</span>
              </div>
              <div className="h-3 bg-white/20 rounded-full overflow-hidden backdrop-blur">
                <div 
                  className="h-full bg-white rounded-full transition-all duration-1000"
                  style={{ width: `${getLevelProgress()}%` }}
                ></div>
              </div>
              {stats.nextLevel !== 'Max' && (
                <p className="text-white/90 text-sm mt-2">
                  تحتاج {stats.pointsToNextLevel.toLocaleString()} نقطة للوصول إلى {stats.nextLevel}
                </p>
              )}
            </div>
          </div>

          {/* الإحصائيات */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">إجمالي المكتسب</CardTitle>
                <TrendingUp className="w-4 h-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.totalEarned.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground mt-1">نقطة</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">إجمالي المستبدل</CardTitle>
                <Gift className="w-4 h-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">{stats.totalRedeemed.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground mt-1">نقطة</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">الرصيد المتبقي</CardTitle>
                <Star className="w-4 h-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{stats.currentPoints.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground mt-1">نقطة</p>
              </CardContent>
            </Card>
          </div>

          {/* المستويات */}
          <Card>
            <CardHeader>
              <CardTitle>مستويات الولاء</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[
                  { name: 'Bronze', min: 0, icon: <Star className="w-6 h-6" />, color: 'from-orange-400 to-orange-600' },
                  { name: 'Silver', min: 1000, icon: <Medal className="w-6 h-6" />, color: 'from-gray-300 to-gray-500' },
                  { name: 'Gold', min: 5000, icon: <Award className="w-6 h-6" />, color: 'from-yellow-400 to-amber-600' },
                  { name: 'Platinum', min: 10000, icon: <Trophy className="w-6 h-6" />, color: 'from-purple-500 to-indigo-600' }
                ].map((level) => (
                  <div 
                    key={level.name}
                    className={`p-4 rounded-xl bg-gradient-to-br ${level.color} text-white ${
                      stats.level === level.name ? 'ring-4 ring-offset-2 ring-blue-500' : 'opacity-70'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {level.icon}
                      <h3 className="font-bold">{level.name}</h3>
                    </div>
                    <p className="text-sm">{level.min.toLocaleString()}+ نقطة</p>
                    {stats.level === level.name && (
                      <p className="text-xs mt-2 font-semibold">المستوى الحالي</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* سجل النقاط */}
            <Card>
              <CardHeader>
                <CardTitle>سجل النقاط</CardTitle>
              </CardHeader>
              <CardContent>
                {stats.history.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">لا توجد نقاط بعد</p>
                ) : (
                  <div className="space-y-3">
                    {stats.history.slice(0, 10).map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-semibold">{item.reason}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(item.createdAt).toLocaleDateString('ar-EG')}
                          </p>
                        </div>
                        <div className={`font-bold ${item.type === 'earn' ? 'text-green-600' : 'text-red-600'}`}>
                          {item.type === 'earn' ? '+' : '-'}{item.points}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* لوحة المتصدرين */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  لوحة المتصدرين
                </CardTitle>
              </CardHeader>
              <CardContent>
                {leaderboard.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">لا يوجد عملاء بعد</p>
                ) : (
                  <div className="space-y-3">
                    {leaderboard.map((item, index) => (
                      <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                            index === 0 ? 'bg-yellow-500' :
                            index === 1 ? 'bg-gray-400' :
                            index === 2 ? 'bg-orange-600' :
                            'bg-gray-300'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-semibold">{item.name}</p>
                            <p className="text-xs text-muted-foreground">{item.level}</p>
                          </div>
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-orange-600">{item.points.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">نقطة</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-muted-foreground">لم يتم العثور على بيانات</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
